const ASYNC_STORAGES = /* @__PURE__ */ new Set(["indexeddb"]);
const isAsyncStorage = (storage) => ASYNC_STORAGES.has(storage ?? "local");
const PERSIST_WRAPPED = Symbol.for("simple-web-component:persist-wrapped");
const winOf = (inst) => {
  try {
    return inst?.ownerDocument?.defaultView ?? globalThis;
  } catch {
    return globalThis;
  }
};
const storeOf = (w, storage) => {
  try {
    if (storage === "local")
      return w?.localStorage ?? null;
    if (storage === "session")
      return w?.sessionStorage ?? null;
    return null;
  } catch {
    return null;
  }
};
const readCookie = (w, key) => {
  try {
    const doc = w?.document ?? globalThis?.document;
    if (!doc?.cookie && doc?.cookie !== "")
      return null;
    const parts = String(doc.cookie).split(";");
    for (const p of parts) {
      const i = p.indexOf("=");
      const k = p.slice(0, i).trim();
      if (k === key)
        return decodeURIComponent(p.slice(i + 1).trim());
    }
    return null;
  } catch {
    return null;
  }
};
const writeCookie = (w, key, raw, opts = {}) => {
  const doc = w?.document ?? globalThis?.document;
  if (!doc)
    return;
  let c = `${key}=${encodeURIComponent(raw)}; path=${opts.path ?? "/"}`;
  if (opts.domain)
    c += `; domain=${opts.domain}`;
  if (opts.maxAge !== void 0)
    c += `; max-age=${opts.maxAge}`;
  if (opts.expires)
    c += `; expires=${opts.expires.toUTCString()}`;
  if (opts.secure)
    c += "; secure";
  if (opts.sameSite)
    c += `; samesite=${opts.sameSite}`;
  try {
    doc.cookie = c;
  } catch {
  }
};
const eraseCookie = (w, key, opts = {}) => {
  writeCookie(w, key, "", { path: opts.path ?? "/", domain: opts.domain, expires: /* @__PURE__ */ new Date(0) });
};
const IDB_DB = "swc-persist";
const IDB_STORE = "kv";
const idbConns = /* @__PURE__ */ new WeakMap();
const idbOpen = (w) => {
  const idb = w?.indexedDB ?? globalThis?.indexedDB;
  if (!idb)
    return Promise.reject(new Error("[SWC] indexedDB unavailable"));
  const wKey = w ?? globalThis;
  let p = idbConns.get(wKey);
  if (!p) {
    p = new Promise((resolve, reject) => {
      const req = idb.open(IDB_DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    p.catch(() => idbConns.delete(wKey));
    idbConns.set(wKey, p);
  }
  return p;
};
const idbTx = async (w, mode, fn) => {
  const db = await idbOpen(w);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, mode);
    const req = fn(tx.objectStore(IDB_STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};
const idbGet = (w, key) => idbTx(w, "readonly", (s) => s.get(key)).then((v) => v === void 0 ? null : v);
const idbSet = (w, key, raw) => idbTx(w, "readwrite", (s) => s.put(raw, key)).then(() => void 0);
const idbDel = (w, key) => idbTx(w, "readwrite", (s) => s.delete(key)).then(() => void 0);
const defSer = (v) => JSON.stringify(v);
const defDe = (s) => {
  try {
    return JSON.parse(s);
  } catch {
    return s;
  }
};
async function persistReadAsync(inst, key, opts) {
  const storage = opts?.storage ?? "local";
  if (!isAsyncStorage(storage))
    return persistRead(inst, key, opts);
  const de = opts?.deserialize ?? defDe;
  const raw = await idbGet(winOf(inst), key);
  if (raw === null || raw === void 0)
    return void 0;
  try {
    return de(raw);
  } catch {
    return raw;
  }
}
async function persistWriteAsync(inst, key, value, opts) {
  const storage = opts?.storage ?? "local";
  if (!isAsyncStorage(storage)) {
    persistWrite(inst, key, value, opts);
    return;
  }
  if (value === void 0) {
    await persistRemoveAsync(inst, key, opts);
    return;
  }
  const se = opts?.serialize ?? defSer;
  await idbSet(winOf(inst), key, se(value));
}
async function persistRemoveAsync(inst, key, opts = {}) {
  const storage = opts?.storage ?? "local";
  if (!isAsyncStorage(storage)) {
    persistRemove(inst, key, opts);
    return;
  }
  await idbDel(winOf(inst), key);
}
const warnedAsyncField = /* @__PURE__ */ new WeakSet();
const isPromiseLike = (v) => v instanceof Promise || v && typeof v.then === "function";
function persistRead(inst, key, opts) {
  const storage = opts?.storage ?? "local";
  const de = opts?.deserialize ?? defDe;
  const w = winOf(inst);
  let raw = null;
  if (storage === "cookie")
    raw = readCookie(w, key);
  else
    raw = storeOf(w, storage)?.getItem(key) ?? null;
  if (raw === null || raw === void 0)
    return void 0;
  try {
    return de(raw);
  } catch {
    return raw;
  }
}
function persistWrite(inst, key, value, opts) {
  const storage = opts?.storage ?? "local";
  if (value === void 0) {
    persistRemove(inst, key, opts);
    return;
  }
  const se = opts?.serialize ?? defSer;
  const raw = se(value);
  const w = winOf(inst);
  if (storage === "cookie")
    writeCookie(w, key, raw, opts);
  else {
    try {
      storeOf(w, storage)?.setItem(key, raw);
    } catch (e) {
      console.warn("[SWC] persistState write failed:", e);
    }
  }
}
function persistRemove(inst, key, opts = {}) {
  const storage = opts?.storage ?? "local";
  const w = winOf(inst);
  if (storage === "cookie")
    eraseCookie(w, key, opts);
  else {
    try {
      storeOf(w, storage)?.removeItem(key);
    } catch {
    }
  }
}
const pickValue = (ret, valueKey) => valueKey !== void 0 ? ret && typeof ret === "object" ? ret[valueKey] : void 0 : ret;
function defineField(targetObj, propertyKey, key, opts) {
  const slot = Symbol(`persist:${String(propertyKey)}`);
  const initialized = Symbol(`persist:init:${String(propertyKey)}`);
  const storage = opts?.storage ?? "local";
  const async = isAsyncStorage(storage);
  if (async && !warnedAsyncField.has(targetObj)) {
    warnedAsyncField.add(targetObj);
    console.warn(`[SWC] @persistState(${storage}) on field "${String(propertyKey)}" resolves asynchronously: first read returns the initializer, the stored value lands right after. Prefer calling persistReadAsync directly or Promise-typed fields for async backends.`);
  }
  Object.defineProperty(targetObj, propertyKey, {
    get() {
      const own = Object.getOwnPropertyDescriptor(this, propertyKey);
      if (own && "value" in own) {
        delete this[propertyKey];
        const initVal = own.value;
        if (async && isPromiseLike(initVal)) {
          this[slot] = initVal;
          this[initialized] = true;
          void persistReadAsync(this, key, opts).then((stored) => {
            if (stored !== void 0) {
              this[slot] = Promise.resolve(stored);
            } else {
              void initVal.then((v) => {
                this[slot] = v;
                void persistWriteAsync(this, key, v, opts).catch((e) => console.warn("[SWC] persistState write failed:", e));
              });
            }
          }).catch(() => void 0);
          return this[slot];
        }
        this[slot] = initVal;
        this[initialized] = true;
        if (async) {
          void persistReadAsync(this, key, opts).then((stored) => {
            if (stored !== void 0)
              this[slot] = stored;
            else
              void persistWriteAsync(this, key, this[slot], opts).catch((e) => console.warn("[SWC] persistState write failed:", e));
          }).catch(() => void 0);
        } else {
          const stored = persistRead(this, key, opts);
          if (stored !== void 0)
            this[slot] = stored;
          else
            persistWrite(this, key, this[slot], opts);
        }
        return this[slot];
      }
      if (!this[initialized]) {
        this[initialized] = true;
        if (async) {
          void persistReadAsync(this, key, opts).then((stored) => {
            if (stored !== void 0)
              this[slot] = stored;
            else if (this[slot] !== void 0)
              void persistWriteAsync(this, key, this[slot], opts).catch((e) => console.warn("[SWC] persistState write failed:", e));
          }).catch(() => void 0);
        } else {
          const stored = persistRead(this, key, opts);
          this[slot] = stored !== void 0 ? stored : this[slot];
          if (stored === void 0 && this[slot] !== void 0)
            persistWrite(this, key, this[slot], opts);
        }
      }
      return this[slot];
    },
    set(nv) {
      if (async) {
        const firstCall = !this[initialized];
        this[slot] = nv;
        this[initialized] = true;
        if (firstCall) {
          void persistReadAsync(this, key, opts).then((stored) => {
            if (stored !== void 0) {
              this[slot] = isPromiseLike(nv) ? Promise.resolve(stored) : stored;
              return;
            }
            if (isPromiseLike(nv)) {
              void nv.then((v) => {
                this[slot] = v;
                void persistWriteAsync(this, key, v, opts).catch((e) => console.warn("[SWC] persistState write failed:", e));
              });
            } else if (nv !== void 0) {
              void persistWriteAsync(this, key, nv, opts).catch((e) => console.warn("[SWC] persistState write failed:", e));
            }
          }).catch(() => void 0);
          return;
        }
        if (isPromiseLike(nv)) {
          void nv.then((v) => {
            this[slot] = v;
            void persistWriteAsync(this, key, v, opts).catch((e) => console.warn("[SWC] persistState write failed:", e));
          });
        } else if (nv === void 0) {
          void persistRemoveAsync(this, key, opts).catch(() => void 0);
        } else {
          void persistWriteAsync(this, key, nv, opts).catch((e) => console.warn("[SWC] persistState write failed:", e));
        }
        return;
      }
      if (!this[initialized]) {
        const stored = persistRead(this, key, opts);
        this[initialized] = true;
        if (stored !== void 0) {
          this[slot] = stored;
          return;
        }
      }
      this[slot] = nv;
      this[initialized] = true;
      persistWrite(this, key, nv, opts);
    },
    enumerable: true,
    configurable: true
  });
}
function wrapMethod(targetObj, propertyKey, descriptor, key, opts) {
  const original = descriptor.value;
  if (original[PERSIST_WRAPPED])
    return descriptor;
  const storage = opts?.storage ?? "local";
  const async = isAsyncStorage(storage);
  const wrapped = function(...args) {
    const res = original.apply(this, args);
    const doPersist = (v) => {
      const val = pickValue(v, opts?.valueKey);
      if (val === void 0)
        return;
      if (async)
        void persistWriteAsync(this, key, val, opts).catch((e) => console.warn("[SWC] persistState write failed:", e));
      else
        persistWrite(this, key, val, opts);
    };
    if (res instanceof Promise)
      return res.then((v) => {
        doPersist(v);
        return v;
      });
    doPersist(res);
    return res;
  };
  wrapped[PERSIST_WRAPPED] = true;
  descriptor.value = wrapped;
  return descriptor;
}
function persistState(keyOrTarget, optionsOrKey, descriptor) {
  if (typeof keyOrTarget === "object" && (typeof optionsOrKey === "string" || typeof optionsOrKey === "symbol")) {
    const targetObj = keyOrTarget;
    const propertyKey = optionsOrKey;
    if (descriptor)
      return wrapMethod(targetObj, propertyKey, descriptor, String(propertyKey), { storage: "local" });
    defineField(targetObj, propertyKey, String(propertyKey), { storage: "local" });
    return;
  }
  const key = typeof keyOrTarget === "string" ? keyOrTarget : void 0;
  const opts = typeof keyOrTarget === "object" && keyOrTarget !== null ? {} : optionsOrKey ?? {};
  return (targetObj, propertyKey, desc) => {
    const k = key ?? String(propertyKey);
    if (desc)
      return wrapMethod(targetObj, propertyKey, desc, k, opts);
    defineField(targetObj, propertyKey, k, opts);
  };
}
const alias = (storage, extra = {}) => {
  const fn = (a, b, c) => {
    if (typeof b === "string" || typeof b === "symbol") {
      if (c)
        return wrapMethod(a, b, c, String(b), { storage, ...extra });
      defineField(a, b, String(b), { storage, ...extra });
      return;
    }
    if (typeof a === "string")
      return persistState(a, { ...b ?? {}, storage, ...extra });
    if (a && typeof a === "object" && b === void 0 && c === void 0) {
      return persistState(void 0, { ...a ?? {}, storage, ...extra });
    }
    return persistState(void 0, { storage, ...extra })(a, b, c);
  };
  return fn;
};
const localStorage = alias("local");
const sessionStorage = alias("session");
const cookie = alias("cookie", { path: "/" });
const indexedDb = alias("indexeddb");
function removePersistState(a, b, c) {
  if (c) {
    const propertyKey = b;
    const orig = c.value;
    c.value = function(...args) {
      const res = orig.apply(this, args);
      const doRemove = (v) => {
        const go = a?.valueKey !== void 0 ? v && typeof v === "object" ? v[a.valueKey] : void 0 : true;
        if (!go)
          return;
        persistRemove(this, String(propertyKey), a ?? {});
      };
      if (res instanceof Promise)
        return res.then((v) => {
          doRemove(v);
          return v;
        });
      doRemove(res);
      return res;
    };
    return c;
  }
  const key = typeof a === "string" ? a : void 0;
  const opts = typeof a === "object" && a !== null ? a : b ?? {};
  return (target, propertyKey, descriptor) => {
    const k = key ?? String(propertyKey);
    const orig = descriptor.value;
    descriptor.value = function(...args) {
      const res = orig.apply(this, args);
      const doRemove = (v) => {
        const vk = opts?.valueKey;
        const go = vk !== void 0 ? v && typeof v === "object" ? v[vk] : void 0 : true;
        if (!go)
          return;
        persistRemove(this, k, opts);
      };
      if (res instanceof Promise)
        return res.then((v) => {
          doRemove(v);
          return v;
        });
      doRemove(res);
      return res;
    };
    return descriptor;
  };
}
const removeAlias = (storage) => (a, b, c) => {
  if (c) {
    const key = String(b);
    return removePersistState(key, { storage })(a, b, c);
  }
  if (typeof a === "string")
    return removePersistState(a, { ...b ?? {}, storage });
  if (a && typeof a === "object")
    return removePersistState(void 0, { ...a ?? {}, storage });
  return (t, pk, d) => removePersistState(String(pk), { storage })(t, pk, d);
};
const removeLocalStorage = removeAlias("local");
const removeSessionStorage = removeAlias("session");
const removeCookie = removeAlias("cookie");
const removeIndexedDb = removeAlias("indexeddb");
export {
  ASYNC_STORAGES,
  cookie,
  indexedDb,
  isAsyncStorage,
  localStorage,
  persistRead,
  persistReadAsync,
  persistRemove,
  persistRemoveAsync,
  persistState,
  persistWrite,
  persistWriteAsync,
  removeCookie,
  removeIndexedDb,
  removeLocalStorage,
  removePersistState,
  removeSessionStorage,
  sessionStorage
};
//# sourceMappingURL=persistState.js.map
